import { useState } from "react";
import { ArrowLeft, Loader2, Trash2 } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";
import {
  IncidentSeveritySchema,
  JournalEntryInputSchema,
  WEATHER_LABELS,
  WeatherSchema,
  type IncidentSeverity,
  type JournalEntry,
  type Weather,
} from "@gb/contracts";
import { ContentColumn } from "@/app/app-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Field } from "@/components/gb/field";
import { SectionLabel } from "@/components/gb/section-label";
import { JournalPhotos } from "./journal-photos";
import {
  useCreateJournalEntry,
  useDeleteJournalEntry,
  useJournalEntry,
  usePatchJournalEntry,
} from "./journals-api";
import { hiddenIncidentCount, mergeIncidents } from "./incidents";

type Errors = Partial<Record<"date" | "headcount" | "weather" | "workCompleted", string>>;

/** Today as `YYYY-MM-DD`, which is what the date input and the API both use. */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * File or amend a day's record.
 *
 * Validation is the shared `JournalEntryInputSchema` rather than a form library
 * — the same approach `StepBasics` takes, and for the same reason: the message
 * the user sees is the rule the server actually enforces.
 *
 * `date` is only editable while creating. The server rejects a patched date
 * outright, because one entry per site per day is a database constraint and
 * moving an entry either collides or silently rewrites which day was signed off.
 */
export function JournalEntryForm({ projectId }: { projectId: string }) {
  const { entryId } = useParams();
  const editing = Boolean(entryId && entryId !== "new");
  const existing = useJournalEntry(projectId, editing ? entryId : undefined);

  if (editing && existing.isPending) {
    return (
      <ContentColumn>
        <div className="flex items-center gap-2 py-16 text-muted" role="status" aria-live="polite">
          <Loader2 size={16} className="animate-spin" />
          <span className="text-[0.9rem]">Loading the entry…</span>
        </div>
      </ContentColumn>
    );
  }

  if (editing && existing.isError) {
    return (
      <ContentColumn>
        <div className="rounded-card-lg border-[1.5px] border-danger/40 bg-danger-bg px-6 py-5">
          <p className="text-[0.92rem] font-semibold text-danger">Couldn’t load this entry.</p>
          <p className="mt-1 text-[0.85rem] text-ink-slate">{existing.error.message}</p>
        </div>
      </ContentColumn>
    );
  }

  return (
    <Form
      projectId={projectId}
      // Remounts when the loaded entry arrives, so the fields initialise from
      // it rather than needing an effect to copy server state into local state.
      key={existing.data?.id ?? "new"}
      entry={editing ? existing.data : undefined}
    />
  );
}

function Form({ projectId, entry }: { projectId: string; entry?: JournalEntry }) {
  const navigate = useNavigate();
  const editing = Boolean(entry);

  const [date, setDate] = useState(entry?.date ?? todayIso());
  const [headcount, setHeadcount] = useState(String(entry?.headcount ?? ""));
  const [weather, setWeather] = useState<Weather | "">(entry?.weather ?? "");
  const [workCompleted, setWorkCompleted] = useState(entry?.workCompleted ?? "");
  const [officeNotes, setOfficeNotes] = useState(entry?.officeNotes ?? "");
  const [incidentSeverity, setIncidentSeverity] = useState<IncidentSeverity>(
    entry?.incidents[0]?.severity ?? "none",
  );
  const [incidentText, setIncidentText] = useState(entry?.incidents[0]?.text ?? "");
  const [errors, setErrors] = useState<Errors>({});

  const hidden = hiddenIncidentCount(entry?.incidents ?? []);

  const create = useCreateJournalEntry(projectId);
  const patch = usePatchJournalEntry(projectId);
  const remove = useDeleteJournalEntry(projectId);

  const busy = create.isPending || patch.isPending || remove.isPending;
  const failure = create.error ?? patch.error ?? remove.error;

  const submit = async (): Promise<void> => {
    // Carries through any incident after the first, which this form does not
    // show. Rebuilding the array from the two fields would delete them.
    const incidents = mergeIncidents(entry?.incidents ?? [], incidentSeverity, incidentText);

    // Blank stays blank. `Number("")` is 0, so the previous version quietly
    // filed "nobody on site" for a field the user had not reached yet — which
    // is precisely the invented fact that made these columns nullable. A day
    // may now be saved half-written; `approve` is what refuses to sign one off.
    const parsed = JournalEntryInputSchema.safeParse({
      date,
      headcount: headcount.trim() === "" ? null : Number(headcount),
      weather: weather === "" ? null : weather,
      workCompleted: workCompleted.trim() || null,
      incidents,
      ...(officeNotes.trim() ? { officeNotes: officeNotes.trim() } : {}),
    });

    if (!parsed.success) {
      const next: Errors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (key === "date" || key === "headcount" || key === "weather" || key === "workCompleted") {
          next[key] = issue.message;
        }
      }
      setErrors(next);
      return;
    }
    setErrors({});

    if (entry) {
      // `date` is omitted: the server rejects it, and the field is disabled.
      const { date: _ignored, ...patchable } = parsed.data;
      await patch.mutateAsync({ entryId: entry.id, patch: patchable });
      void navigate(`/projects/${projectId}/journal/${entry.id}`);
      return;
    }

    const created = await create.mutateAsync(parsed.data);
    void navigate(`/projects/${projectId}/journal/${created.id}`);
  };

  return (
    <ContentColumn>
      <Link
        to={
          entry
            ? `/projects/${projectId}/journal/${entry.id}`
            : `/projects/${projectId}/journal`
        }
        className="inline-flex items-center gap-1.5 text-[0.85rem] font-semibold text-muted transition-colors hover:text-ink"
      >
        <ArrowLeft size={15} />
        {entry ? "Back to the entry" : "All entries"}
      </Link>

      <h1 className="mt-4 mb-6 font-display text-[1.7rem] font-bold uppercase leading-[1.05] tracking-[0.01em] text-ink">
        {editing ? "Amend the day’s record" : "File the day’s record"}
      </h1>

      <section className="rounded-card-lg border-[1.5px] border-border bg-surface p-6 shadow-flat">
        <div className="grid gap-[18px] sm:grid-cols-2">
          <Field label="Date" error={errors.date}>
            {(props) => (
              <Input
                {...props}
                type="date"
                value={date}
                // One entry per site per day is a database constraint, so an
                // amend cannot move the day — it would either collide or
                // rewrite which day was signed off.
                disabled={editing}
                onChange={(e) => setDate(e.target.value)}
              />
            )}
          </Field>

          <Field label="On site" error={errors.headcount}>
            {(props) => (
              <Input
                {...props}
                type="number"
                min={0}
                inputMode="numeric"
                value={headcount}
                onChange={(e) => setHeadcount(e.target.value)}
              />
            )}
          </Field>

          <Field label="Weather" error={errors.weather}>
            {(props) => (
              <Select value={weather} onValueChange={(v) => setWeather(v as Weather)}>
                <SelectTrigger {...props}>
                  <SelectValue placeholder="Choose…" />
                </SelectTrigger>
                <SelectContent>
                  {WeatherSchema.options.map((w) => (
                    <SelectItem key={w} value={w}>
                      {WEATHER_LABELS[w]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          <Field label="Incidents">
            {(props) => (
              <Select
                value={incidentSeverity}
                onValueChange={(v) => setIncidentSeverity(v as IncidentSeverity)}
              >
                <SelectTrigger {...props}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {IncidentSeveritySchema.options.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s === "none" ? "None" : s === "minor" ? "Minor" : "Reportable"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          <Field label="Work completed" error={errors.workCompleted} className="sm:col-span-2">
            {(props) => (
              <Textarea
                {...props}
                rows={4}
                value={workCompleted}
                placeholder="Sector B1: module install rows 18–22…"
                onChange={(e) => setWorkCompleted(e.target.value)}
              />
            )}
          </Field>

          <Field
            label="Incident detail"
            className="sm:col-span-2"
            hint={
              hidden > 0
                ? `${hidden} further ${hidden === 1 ? "incident is" : "incidents are"} recorded on this day and kept as they are — this form edits the first.`
                : undefined
            }
          >
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                value={incidentText}
                placeholder="What happened, and what was done about it."
                onChange={(e) => setIncidentText(e.target.value)}
              />
            )}
          </Field>

          <Field label="Notes" className="sm:col-span-2">
            {(props) => (
              <Textarea
                {...props}
                rows={6}
                value={officeNotes}
                placeholder="Anything the day should be remembered for. Blank lines separate paragraphs."
                onChange={(e) => setOfficeNotes(e.target.value)}
              />
            )}
          </Field>
        </div>
      </section>

      {/* Only when the day exists. Photographs are uploaded one at a time
          against an entry id, so there is nothing to attach them to until the
          record has been created — and the journal list's "Add photos" opens a
          day precisely so this is not the way in. */}
      {entry && (
        <section className="mt-6">
          <SectionLabel>Photos</SectionLabel>
          <div className="mt-2.5 rounded-card-lg border-[1.5px] border-border bg-surface p-5">
            <JournalPhotos projectId={projectId} entryId={entry.id} />
          </div>
        </section>
      )}

      {failure && (
        <p
          role="alert"
          className="mt-4 rounded-input border-[1.5px] border-danger/40 bg-danger-bg px-4 py-3 text-[0.85rem] font-semibold text-danger"
        >
          {failure.message}
        </p>
      )}

      <div className="mt-5 flex items-center justify-between gap-4">
        {entry ? (
          <Button
            variant="secondary"
            type="button"
            disabled={busy}
            onClick={async () => {
              await remove.mutateAsync(entry.id);
              void navigate(`/projects/${projectId}/journal`);
            }}
          >
            <Trash2 size={15} />
            Delete
          </Button>
        ) : (
          <span />
        )}
        <Button
          type="button"
          className="hover:bg-brand-deep"
          disabled={busy}
          onClick={() => void submit()}
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : null}
          {editing ? "Save changes" : "File the record"}
        </Button>
      </div>
    </ContentColumn>
  );
}
